import { component } from '../core'
import { animateOpen, animateClose } from '../utils'
import { isShiftTab, isTabOnly } from '../utils'
import { getFirstAndLastFocusableElement } from '../utils/firstlast-focusable-elements'
import { isRTL } from '../../../../gbds-core/src/ts/utils/helper-utils'

export enum TabNavEvents {
	stackedTabClosed = 'gb-stackedTabClosed',
	stackedTabOpened = 'gb-stackedTabOpened',
}

interface TabContent {
	tabButton: HTMLElement
	content: HTMLElement
	backButton: HTMLElement
}

/**
 * @noInheritDoc
 */
@component('gb-tab-nav')
export class TabNav extends HTMLElement {
	private _tabButtons: Array<HTMLElement>
	private _contentContainers: Array<HTMLElement>
	private _backButtons: Array<HTMLElement>
	private _tabContainerMap = new Map<string, TabContent>()
	private _firstFocusableElement: HTMLElement
	private _lastFocusableElement: HTMLElement

	constructor() {
		super()
	}

	async onInit() {
		this._tabButtons = Array.from(this.querySelectorAll<HTMLElement>('.content-button button'))
		this._contentContainers = Array.from(this.querySelectorAll<HTMLElement>('.content-container'))
		this._backButtons = Array.from(this.querySelectorAll<HTMLElement>('.content-container .back-button'))

		// TODO: Nice to have. Check for duplicate ids

		this._multipleDefaultCheck()

		this._tabButtons.forEach(btn => {
			const content = this._contentContainers.find(cnt => cnt.dataset.contentId === btn.dataset.tabId)
			const backButton = this._backButtons.find(bck => bck.parentElement.dataset.contentId === btn.dataset.tabId)

			// TODO: Nice to Have. Have a way to handle if either of the variables above are undefined

			this._tabContainerMap.set(btn.dataset.tabId, { tabButton: btn, content, backButton })
		})

		if (this.defaultTab) {
			this.defaultTab.parentElement.classList.toggle('active')
		}

		if (!this.isStackedView) {
			this.toggleTab(this.defaultTab)
		} else {
			this._tabContainerMap.get(this.defaultTab.dataset.tabId).content.classList.add('visible')
		}

		this._addTabListeners()

		if (isRTL()) {
			this.style.setProperty('--rotate-stacked-button-arrow', 'rotate(180deg)') // will flip stacked button arrow icon.
			this.style.setProperty('--rotate-back-button-arrow', 'rotate(0deg)') // will flip back button arrow icon.
		}
	}

	private _multipleDefaultCheck() {
		const defaultTabs = this._tabButtons.filter(tb => tb.parentElement.classList.contains('default'))

		if (defaultTabs.length > 1) {
			console.warn('gb-tab-nav: Multiple tabs set as default. Please only set one tab as the default.')
		}
	}

	private _addTabListeners() {
		this._tabButtons.forEach(btn => {
			btn.addEventListener('click', e => {
				this.toggleTab(e.currentTarget)
				e.preventDefault()
			})
		})
	}

	private _setFirstLastTabbableElements(contentContainer, backButton) {
		const { first, last } = getFirstAndLastFocusableElement(contentContainer)

		// The first and last focusable element default to the close button or back button if none other exist
		this._firstFocusableElement = first || backButton
		this._lastFocusableElement = last || backButton
	}

	/**
	 * Handles the activation of the tab that was selected by the user.
	 * @param tabButton
	 */
	public toggleTab(tabButton): void {
		if (tabButton) {
			const tabID = tabButton.dataset.tabId
			const openTab = this.openTab

			if (openTab) openTab.parentElement.classList.remove('active')
			tabButton.parentElement.classList.toggle('active')

			this.toggleContainer(tabID)
		}
	}

	/**
	 * Handles showing and hiding the content containers based on the tab that was selected by the user.
	 * @param tabID
	 */
	public toggleContainer(tabID): void {
		const contentContainer: HTMLElement = this._tabContainerMap.get(tabID).content
		const backButton: HTMLElement = this._tabContainerMap.get(tabID).backButton
		const hamburgerMenu = this.closest('gb-flyout[data-hamburger-menu="true"]')

		/**
		 * Visible class used on regular layout so stacked content containers
		 * are not open if the view changes from regular to stacked.
		 */
		if (this.openContainer) {
			this.openContainer.classList.remove('visible', 'open')
		}

		if (this.lastOpened) {
			this.lastOpened.classList.remove('last-opened')
		}

		if (this.isStackedView) {
			/**
			 * Determines where the top of the tab-nav is in the window and sets the
			 * content container's 'top' style property to that value in px. animateOpen()
			 * handles sliding content containers into view while in stacked view.
			 */
			let yPosition = this.getBoundingClientRect().y < 0 ? 0 : this.getBoundingClientRect().y

			if (this.isInFlyout) {
				yPosition = this.closest('gb-flyout').getBoundingClientRect().y
			}

			this._setFirstLastTabbableElements(contentContainer, backButton)
			backButton.addEventListener('keydown', e => {
				if (isShiftTab(e)) {
					this.closeTab(tabID)
					e.preventDefault()
				}
			})

			if (hamburgerMenu) {
				const hbMenuCloseBtn = this.closest('gb-flyout[data-hamburger-menu="true"]').querySelector(
					'.gb-flyout-close'
				)

				const hbMenuFlyout = <HTMLElement>hamburgerMenu
				hbMenuFlyout.style.visibility = 'hidden'

				hbMenuCloseBtn.classList.add('gb-in-stacked-hb-close')
				this._lastFocusableElement = <HTMLElement>hbMenuCloseBtn
			}

			this._lastFocusableElement.addEventListener('keydown', e => {
				if (isTabOnly(e) && e.currentTarget !== backButton) {
					backButton.focus()
					e.preventDefault()
				}
			})

			contentContainer.style.top = yPosition + 'px'
			contentContainer.style.height = 'calc(100% - ' + yPosition + 'px)'
			animateOpen(contentContainer)
			this.dispatchEvent(
				new CustomEvent(TabNavEvents.stackedTabOpened, { detail: { openContainer: contentContainer } })
			)
		}

		contentContainer.classList.add('visible')

		backButton.focus()
		backButton.addEventListener('click', () => this.closeTab(tabID))
	}

	/**
	 * Adds the close class which is used to hide a content container in the stacked view, but not hide it
	 * when the component switches to the tab view.
	 * @param contentContainer
	 */
	public closeTab(tabID): void {
		const contentContainer: HTMLElement = this._tabContainerMap.get(tabID).content
		const hamburgerMenu = this.closest('gb-flyout[data-hamburger-menu="true"]')

		if (this.lastOpened !== undefined) {
			this.lastOpened.classList.remove('last-opened')
		}
		contentContainer.classList.remove('visible')
		animateClose(contentContainer)
		this.dispatchEvent(
			new CustomEvent(TabNavEvents.stackedTabClosed, { detail: { closedContainer: contentContainer } })
		)
		/**
		 * Adding 'last-opened' class to content container that was just closed in stacked view
		 * to ensure the content container is still visible if the view is changed to regular view.
		 */
		contentContainer.classList.add('last-opened')
		this._tabContainerMap.get(tabID).tabButton.focus()

		/*
		 * Hamburger Menu requires a close icon to be fixed at top. Inc combination with fixed flyout size.
		 *The close icon has to be set a certain distance from left on certain view ports.
		 */

		if (hamburgerMenu) {
			const hbMenuCloseBtn = this.closest('gb-flyout[data-hamburger-menu="true"]').querySelector(
				'.gb-flyout-close'
			)

			hbMenuCloseBtn.classList.remove('gb-in-stacked-hb-close')

			const hbMenuFlyout = <HTMLElement>hamburgerMenu
			hbMenuFlyout.style.visibility = ''
		}
	}

	public get isStackedView() {
		return this._tabButtons.find(btn => window.getComputedStyle(btn).display !== 'inline-block')
	}

	public get isInFlyout() {
		return this.closest('gb-flyout') != null
	}

	public get openTab() {
		return this._tabButtons.find(btn => btn.parentElement.classList.contains('active'))
	}

	public get defaultTab() {
		return this._tabButtons.find(btn => btn.parentElement.classList.contains('default'))
	}

	public get openContainer() {
		// eslint-disable-next-line no-constant-binary-expression
		return this._contentContainers.find(cnt => cnt.classList.contains('visible' || 'open'))
	}

	public get lastOpened() {
		return this._contentContainers.find(cnt => cnt.classList.contains('last-opened'))
	}
}
